import { css } from '@styled-system/css';
import { Radar, Search, Store } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/shared/components/ui/Button';

import { useCredits } from './useCredits';

// Bloc compact des quotas (recherches produit / vendeur, veilles) affiché en
// bas de la sidebar, au-dessus du compte. Clic -> page abonnement.

const block = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '3',
  padding: '4',
  marginBottom: '1',
  borderRadius: 'lg',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.subtle',
  bg: 'surface.subtle',
  cursor: 'pointer',
  transition: 'border-color 120ms ease, background 120ms ease',
  _hover: { borderColor: 'border.strong', bg: 'surface.sunken' },
});

const row = css({ display: 'flex', flexDirection: 'column', gap: '1.5' });

const rowTop = css({
  display: 'flex',
  alignItems: 'center',
  gap: '2.5',
  fontSize: 'caption',
  color: 'text.tertiary',
});

const rowValue = css({
  marginLeft: 'auto',
  fontFamily: 'mono',
  fontSize: 'caption',
  fontWeight: 'semibold',
});

const track = css({
  width: '100%',
  height: '3px',
  borderRadius: 'full',
  bg: 'surface.sunken',
  overflow: 'hidden',
});

const trialChip = css({
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  paddingY: '0.5',
  paddingX: '2.5',
  borderRadius: 'full',
  fontSize: 'micro',
  fontWeight: 'medium',
  bg: 'brand.primary/10',
  color: 'brand.primary',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'brand.primary/40',
  whiteSpace: 'nowrap',
});

const UsageRow: FC<{ icon: ReactNode; label: string; used: number; max: number }> = ({ icon, label, used, max }) => {
  const ratio = max === 0 ? 0 : Math.min(1, used / max);
  const color =
    used >= max
      ? 'var(--colors-error-default)'
      : ratio > 0.8
        ? 'var(--colors-warning-default)'
        : 'var(--colors-brand-primary)';
  return (
    <div className={row}>
      <span className={rowTop}>
        {icon}
        {label}
        <span className={rowValue} style={{ color: used >= max ? 'var(--colors-error-default)' : undefined }}>
          {used}/{max}
        </span>
      </span>
      <div className={track}>
        <div style={{ width: `${ratio * 100}%`, height: '100%', background: color, transition: 'width 200ms ease' }} />
      </div>
    </div>
  );
};

export const CreditUsageSidebar: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const usage = useCredits();

  // CTA visible dès qu'au moins un quota est au max (données serveur chargées)
  const anyQuotaMaxed =
    usage.ready &&
    ((usage.limits.product > 0 && usage.used.product >= usage.limits.product) ||
      (usage.limits.vendor > 0 && usage.used.vendor >= usage.limits.vendor) ||
      (usage.veille.max > 0 && usage.veille.used >= usage.veille.max));

  return (
    <div
      className={block}
      role="button"
      tabIndex={0}
      onClick={() => navigate('/subscription')}
      onKeyDown={(event) => {
        if (event.key === 'Enter') navigate('/subscription');
      }}
      aria-label={t('credits.sidebarAria')}
    >
      <UsageRow
        icon={<Search size={11} strokeWidth={1.75} />}
        label={t('credits.product')}
        used={usage.used.product}
        max={usage.limits.product}
      />
      <UsageRow
        icon={<Store size={11} strokeWidth={1.75} />}
        label={t('credits.vendor')}
        used={usage.used.vendor}
        max={usage.limits.vendor}
      />
      <UsageRow
        icon={<Radar size={11} strokeWidth={1.75} />}
        label={t('credits.veille')}
        used={usage.veille.used}
        max={usage.veille.max}
      />
      {usage.trialing && <span className={trialChip}>{t('credits.trialChip', { days: usage.trialDaysLeft })}</span>}
      {anyQuotaMaxed && (
        <Button
          variant="cta"
          size="sm"
          className={css({ width: 'full' })}
          onClick={(event) => {
            event.stopPropagation();
            navigate('/subscription');
          }}
        >
          {usage.trialing ? t('credits.ctaTrial') : t('credits.ctaUpgrade')}
        </Button>
      )}
    </div>
  );
};
